import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { Mail, KeyRound, AlertCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { NETWORK_ERROR_MESSAGE } from "@/utils/axios";
import { ThirdPartyLogin } from "./ThirdPartyLogin";

// Email form validation schema
const emailFormSchema = z.object({
  email: z.string().email(
    "Please enter a valid email address"
  ),
});

// Verification code form validation schema
const codeFormSchema = z.object({
  code: z.string().min(4, "Verification code must be at least 4 digits"),
});

interface CodeLoginFormProps {
  onLoginSuccess?: (data: any, email: string) => void;
  onAgreementError?: () => void;
  initialEmail?: string;
  redirectUrl?: string;
  agreementAccepted?: boolean;
}

const CodeLoginForm = ({ onLoginSuccess, onAgreementError, initialEmail = "", redirectUrl = "", agreementAccepted = false }: CodeLoginFormProps = {}) => {
  const [step, setStep] = useState<"email" | "code">("email");
  const [userEmail, setUserEmail] = useState(initialEmail || localStorage.getItem('anzir_lastEmail') || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const { sendOtp, loginWithCode, checkEmailExists } = useAuth();
  const { t } = useTranslation();

  // 当initialEmail变化时更新userEmail
  useEffect(() => {
    if (initialEmail) {
      setUserEmail(initialEmail);
    }
  }, [initialEmail]);

  // Email form
  const emailForm = useForm<z.infer<typeof emailFormSchema>>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email: initialEmail || localStorage.getItem('anzir_lastEmail') || "",
    },
  });

  // 当initialEmail变化时更新表单
  useEffect(() => {
    if (initialEmail) {
      emailForm.setValue('email', initialEmail);
    }
  }, [initialEmail, emailForm]);

  // Verification code form
  const codeForm = useForm<z.infer<typeof codeFormSchema>>({
    resolver: zodResolver(codeFormSchema),
    defaultValues: {
      code: "",
    },
  });

  // Request verification code
  const handleRequestCode = async (values: z.infer<typeof emailFormSchema>) => {
    setError("");
    
    if (!agreementAccepted) {
      setError("You must agree to our terms and conditions");
      toast.error("Terms not accepted", {
        description: "You must agree to our terms and conditions to continue"
      });
      if (onAgreementError) {
        onAgreementError();
      }
      return;
    }
    
    setIsSubmitting(true);

    try {
      // 保存邮箱到localStorage
      localStorage.setItem('anzir_lastEmail', values.email);
      
      // 更新userEmail，确保后续使用的是当前邮箱
      setUserEmail(values.email);
      
      // First check if the email exists
      const emailExists = await checkEmailExists(values.email);
      
      if (!emailExists) {
        setError("Email address not found. Please check your email or sign up.");
        toast.error("Email Not Found", {
          description: "This email address is not registered. Please check your email or create an account.",
        });
        setIsSubmitting(false);
        return;
      }
      
      // If email exists, send verification code
      await sendOtp(values.email);
      setStep("code");
      toast.success(t("auth.code.sent", "Verification Code Sent"), {
        description: t("auth.code.sentDescription", "We've sent a code to " + values.email + ". Please check your inbox.", { email: values.email }),
      });
    } catch (error: any) {
      console.error("Send verification code error:", error);
      
      let errorMessage = t("auth.code.sendFailed", "Failed to send verification code. Please try again.");
      
      if (error.message) {
        if (error.message === NETWORK_ERROR_MESSAGE) {
          errorMessage = t("auth.errors.networkError", "Network error, please try again later");
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      toast.error(t("auth.errors.error", "Error"), {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit verification code
  const handleSubmitCode = async (values: z.infer<typeof codeFormSchema>) => {
    setError("");
    
    if (!agreementAccepted) {
      setError("You must agree to our terms and conditions");
      toast.error("Terms not accepted", {
        description: "You must agree to our terms and conditions to continue"
      });
      if (onAgreementError) {
        onAgreementError();
      }
      return;
    }
    
    setIsSubmitting(true);

    try {
      // 检查userEmail是否有值
      if (!userEmail) {
        setError("Email address is missing. Please go back and enter your email.");
        toast.error("Missing Email", {
          description: "Email address is missing. Please go back and enter your email.",
        });
        setIsSubmitting(false);
        return;
      }

      console.log("Submitting code for email:", userEmail, "code:", values.code);
      const response = await loginWithCode(userEmail, values.code);

      toast.success('Login Successful', {
        description: 'You have successfully logged in.',
      });

      if (onLoginSuccess) {
        onLoginSuccess(response.data, userEmail);
      } else {
        // If no callback is provided, check for redirectUrl first
        const targetUrl = redirectUrl || '/profile';
        navigate(targetUrl, { replace: true });
      }
    } catch (error: any) {
      console.error("Code login error:", error);
      
      let errorMessage = t("auth.code.invalid", "Invalid verification code. Please try again.");
      
      if (error.message) {
        if (error.message === NETWORK_ERROR_MESSAGE) {
          errorMessage = t("auth.errors.networkError", "Network error, please try again later");
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      toast.error(t("auth.errors.authError", "Authentication Error"), {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Return to email input step
  const handleBackToEmail = () => {
    setStep("email");
    setError("");
    codeForm.reset();
  };

  // Resend verification code
  const handleResendCode = async () => {
    setError("");
    setIsSubmitting(true);

    // 确保userEmail有值
    if (!userEmail) {
      setError("Email address is missing. Please go back and enter your email.");
      setIsSubmitting(false);
      return;
    }

    try {
      await sendOtp(userEmail);
      toast.success(t("auth.code.resent", "Verification Code Resent"), {
        description: t("auth.code.sentDescription", "We've sent a new code to " + userEmail + ". Please check your inbox.", { email: userEmail }),
      });
    } catch (error: any) {
      console.error("Resend verification code error:", error);
      
      let errorMessage = t("auth.code.sendFailed", "Failed to send verification code. Please try again.");
      
      if (error.message) {
        if (error.message === NETWORK_ERROR_MESSAGE) {
          errorMessage = t("auth.errors.networkError", "Network error, please try again later");
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      toast.error(t("auth.errors.error", "Error"), {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {step === "email" ? (
        <>
          <Form {...emailForm}>
            <form onSubmit={emailForm.handleSubmit(handleRequestCode)} className="space-y-4">
              <FormField
                control={emailForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.email", "Email")}</FormLabel>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                      <FormControl>
                        <Input
                          placeholder={t("auth.emailPlaceholder", "yourname@example.com")}
                          className="pl-10"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {error && (
                <div className="bg-red-500/10 text-red-500 px-4 py-2 rounded flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-[#ff6c24] hover:bg-[#ff6c24]/90"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full inline-block animate-spin mr-2"></span>
                ) : null}
                {t("auth.code.getCode", "Get Verification Code")}
              </Button>
            </form>
          </Form>
          
          <div className="mt-4">
            <ThirdPartyLogin 
              variant="default" 
              size="default" 
              agreementAccepted={agreementAccepted}
              onAgreementError={onAgreementError}
            />
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <button 
            onClick={handleBackToEmail}
            className="flex items-center text-sm text-gray-400 hover:text-white mb-2 transition-colors"
          >
            <ArrowLeft size={16} className="mr-1" />
            {t("auth.code.backToEmail", "Change Email")}
          </button>

          <div className="text-center mb-4">
            <p className="text-sm text-gray-400">
              {t("auth.code.enterCodeSentTo", "Enter the verification code sent to")}
              <br />
              <span className="font-medium text-white">{userEmail}</span>
            </p>
          </div>

          <Form {...codeForm}>
            <form onSubmit={codeForm.handleSubmit(handleSubmitCode)} className="space-y-4">
              <FormField
                control={codeForm.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.code.verificationCode", "Verification Code")}</FormLabel>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                      <FormControl>
                        <Input
                          placeholder={t("auth.code.enterCode", "Enter verification code")}
                          className="pl-10"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {error && (
                <div className="bg-red-500/10 text-red-500 px-4 py-2 rounded flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div className="flex flex-col space-y-2">
                <Button
                  type="submit"
                  className="w-full bg-[#ff6c24] hover:bg-[#ff6c24]/90"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full inline-block animate-spin mr-2"></span>
                  ) : null}
                  {t("auth.login", "Sign In")}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="text-sm text-gray-400 hover:text-white"
                  onClick={handleResendCode}
                  disabled={isSubmitting}
                >
                  {t("auth.code.resendCode", "Resend Code")}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      )}
    </div>
  );
};

export default CodeLoginForm;
